// Mercapoli - Comercio Dashboard con Comisiones Variables y DIGGII
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Package, DollarSign, Clock, CheckCircle, XCircle,
  Store, Bell, LogOut, Menu as MenuIcon, Settings,
  TrendingUp, Wallet, CreditCard, ArrowUpRight, ArrowDownRight,
  Calendar, ChevronRight, Coins, AlertTriangle, RefreshCw, Shield
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { comercio as comercioApi, pedidos as pedidosApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import DiggiiBalance from '../../components/DiggiiBalance';
import DiggiiHistory from '../../components/DiggiiHistory';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mercadelivery-1/artifacts/5y038jeg_icono_mercapoli.png";

const ComercioDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [activeTab, setActiveTab] = useState('pedidos');
  const [showDiggiiHistory, setShowDiggiiHistory] = useState(false);
  const [showAceptarComision, setShowAceptarComision] = useState(false);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [dashData, pedidosData] = await Promise.all([
        comercioApi.getDashboard(),
        comercioApi.getPedidos()
      ]);
      setDashboard(dashData);
      setPedidos(pedidosData);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const updateEstado = async (pedidoId, nuevoEstado) => {
    try {
      await pedidosApi.updateEstado(pedidoId, nuevoEstado);
      toast.success(`Pedido ${nuevoEstado === 'cancelado' ? 'cancelado' : 'actualizado'}`);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar pedido');
    }
  };

  const aceptarComision = async () => {
    try {
      await comercioApi.aceptarComision();
      toast.success('¡Comisión aceptada! Ya puedes recibir pedidos.');
      setShowAceptarComision(false);
      loadData();
    } catch (error) {
      toast.error('Error al aceptar comisión');
    }
  };

  const simbolo = dashboard?.simbolo || 'Q';
  const formatMoney = (amount) => `${simbolo}${amount?.toLocaleString('es-GT', { minimumFractionDigits: 2 }) || '0.00'}`;

  const getStatusColor = (status) => {
    const colors = {
      pendiente: 'bg-yellow-100 text-yellow-800',
      aceptado: 'bg-blue-100 text-blue-800',
      preparando: 'bg-purple-100 text-purple-800',
      listo: 'bg-green-100 text-green-800',
      en_camino: 'bg-cyan-100 text-cyan-800',
      entregado: 'bg-emerald-100 text-emerald-800',
      cancelado: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100';
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const pedidosPendientes = pedidos.filter(p => p.estado === 'pendiente');
  const pedidosActivos = pedidos.filter(p => ['aceptado', 'preparando', 'listo'].includes(p.estado));

  const getPlanBadge = (plan) => {
    if (plan === 'pro') {
      return <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">Plan Pro</Badge>;
    }
    if (plan === 'freemium') {
      return <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">Plan Freemium</Badge>;
    }
    return <Badge variant="outline">Plan Básico</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Mercapoli" className="h-8 w-8" />
              <div>
                <h1 className="font-heading font-bold">{dashboard?.comercio?.nombre || 'Mi Comercio'}</h1>
                <div className="flex items-center gap-2">
                  {dashboard?.comercio && getPlanBadge(dashboard.comercio.plan)}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={loadData}>
                <RefreshCw className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleLogout}>
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-5xl">
        {/* Alerta de comisión pendiente */}
        {dashboard?.comercio && !dashboard.comercio.comision_aceptada && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-bold text-yellow-800 dark:text-yellow-200">
                      Acepta la comisión para empezar a recibir pedidos
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      Tu comisión negociada es del <span className="font-bold">{dashboard.comercio.comision_porcentaje}%</span> sobre cada venta.
                      {dashboard.comercio.plan === 'pro' && ' (Tarifa reducida por Plan Pro)'}
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      Precio del plan: <span className="font-bold">{formatMoney(dashboard.comercio.plan_precio_local)}/mes</span>
                    </p>
                    <Button 
                      className="mt-3 bg-yellow-600 hover:bg-yellow-700"
                      onClick={() => setShowAceptarComision(true)}
                    >
                      <Shield className="h-4 w-4 mr-2" />
                      Aceptar Comisión
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="bg-gradient-to-br from-brand-primary to-brand-secondary text-white">
            <CardContent className="p-3 text-center">
              <DollarSign className="h-5 w-5 mx-auto mb-1 opacity-80" />
              <p className="text-lg font-bold">{formatMoney(dashboard?.estadisticas?.ventas_hoy || 0)}</p>
              <p className="text-xs opacity-80">Ventas hoy</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <Package className="h-5 w-5 mx-auto mb-1 text-brand-primary" />
              <p className="text-lg font-bold">{dashboard?.estadisticas?.pedidos_hoy || 0}</p>
              <p className="text-xs text-muted-foreground">Pedidos hoy</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <TrendingUp className="h-5 w-5 mx-auto mb-1 text-green-500" />
              <p className="text-lg font-bold">{formatMoney(dashboard?.estadisticas?.neto_mes || 0)}</p>
              <p className="text-xs text-muted-foreground">Neto este mes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <Coins className="h-5 w-5 mx-auto mb-1 text-amber-500" />
              <p className="text-lg font-bold">{dashboard?.diggii?.balance?.toFixed(1) || 0}</p>
              <p className="text-xs text-muted-foreground">DIGGII</p>
            </CardContent>
          </Card>
        </div>

        {/* Info de Comisión */}
        <Card className="mb-6 border-blue-500/30 bg-blue-50 dark:bg-blue-950/20">
          <CardContent className="p-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Tu comisión</p>
                <p className="text-xl font-bold text-blue-600">{dashboard?.comercio?.comision_porcentaje || 15}%</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ventas mes</p>
                <p className="text-xl font-bold">{formatMoney(dashboard?.estadisticas?.ventas_mes || 0)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Comisiones mes</p>
                <p className="text-xl font-bold text-red-500">-{formatMoney(dashboard?.estadisticas?.comisiones_mes || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* DIGGII Card */}
        {dashboard?.diggii && dashboard.diggii.balance > 0 && (
          <div className="mb-6">
            <DiggiiBalance
              balance={dashboard.diggii.balance}
              valorLocal={dashboard.diggii.valor_local}
              simbolo={simbolo}
              progresoSiguiente={dashboard.diggii.total_gastado_usd % 200}
              faltaParaSiguiente={200 - (dashboard.diggii.total_gastado_usd % 200)}
              ratioAcumulacion={200}
              onClick={() => setShowDiggiiHistory(true)}
            />
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="pedidos" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Pedidos ({pedidosPendientes.length})
            </TabsTrigger>
            <TabsTrigger value="historial" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Historial
            </TabsTrigger>
          </TabsList>

          {/* Tab Pedidos */}
          <TabsContent value="pedidos">
            {pedidosPendientes.length === 0 && pedidosActivos.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No hay pedidos pendientes</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {[...pedidosPendientes, ...pedidosActivos].map((pedido) => (
                  <Card key={pedido.pedido_id} className={pedido.estado === 'pendiente' ? 'border-yellow-500' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-bold">Pedido #{pedido.pedido_id.slice(-6)}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(pedido.created_at).toLocaleString('es-GT')}
                          </p>
                        </div>
                        <Badge className={getStatusColor(pedido.estado)}>
                          {pedido.estado}
                        </Badge>
                      </div>

                      {/* Items */}
                      <div className="bg-muted p-3 rounded-lg mb-3">
                        {pedido.items?.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span>{item.cantidad}x {item.nombre}</span>
                            <span>{formatMoney(item.subtotal)}</span>
                          </div>
                        ))}
                      </div>

                      {/* Desglose con comisión */}
                      {pedido.desglose_comercio && (
                        <div className="bg-muted/50 p-3 rounded-lg mb-3 text-sm">
                          <div className="flex justify-between">
                            <span>Subtotal:</span>
                            <span>{formatMoney(pedido.desglose_comercio.subtotal)}</span>
                          </div>
                          <div className="flex justify-between text-red-500">
                            <span>Comisión ({pedido.desglose_comercio.comision_porcentaje}%):</span>
                            <span>-{formatMoney(pedido.desglose_comercio.comision_monto)}</span>
                          </div>
                          <div className="flex justify-between font-bold text-green-600 border-t mt-2 pt-2">
                            <span>Tu ganancia:</span>
                            <span>{formatMoney(pedido.desglose_comercio.neto_comercio)}</span>
                          </div>
                        </div>
                      )}

                      {/* Acciones */}
                      {pedido.estado === 'pendiente' && (
                        <div className="flex gap-2">
                          <Button 
                            className="flex-1 bg-green-500 hover:bg-green-600"
                            onClick={() => updateEstado(pedido.pedido_id, 'aceptado')}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Aceptar
                          </Button>
                          <Button 
                            variant="outline"
                            className="border-red-500 text-red-500"
                            onClick={() => updateEstado(pedido.pedido_id, 'cancelado')}
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      {pedido.estado === 'aceptado' && (
                        <Button 
                          className="w-full"
                          onClick={() => updateEstado(pedido.pedido_id, 'preparando')}
                        >
                          Comenzar preparación
                        </Button>
                      )}
                      {pedido.estado === 'preparando' && (
                        <Button 
                          className="w-full bg-green-500 hover:bg-green-600"
                          onClick={() => updateEstado(pedido.pedido_id, 'listo')}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Marcar listo para recoger
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tab Historial */}
          <TabsContent value="historial">
            <div className="space-y-3">
              {pedidos.filter(p => ['entregado', 'cancelado'].includes(p.estado)).slice(0, 20).map((pedido) => (
                <Card key={pedido.pedido_id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Pedido #{pedido.pedido_id.slice(-6)}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(pedido.created_at).toLocaleDateString('es-GT')}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge className={getStatusColor(pedido.estado)}>{pedido.estado}</Badge>
                        {pedido.desglose_comercio && (
                          <p className="text-sm font-bold text-green-600 mt-1">
                            +{formatMoney(pedido.desglose_comercio.neto_comercio)}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal Aceptar Comisión */}
      <AlertDialog open={showAceptarComision} onOpenChange={setShowAceptarComision}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aceptar Términos de Comisión</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Al aceptar, confirmas que estás de acuerdo con los siguientes términos:
              </p>
              <div className="bg-muted p-4 rounded-lg">
                <p><strong>Comisión por venta:</strong> {dashboard?.comercio?.comision_porcentaje}%</p>
                <p><strong>Plan:</strong> {dashboard?.comercio?.plan === 'pro' ? 'Pro' : 'Básico'}</p>
                <p><strong>Precio mensual:</strong> {formatMoney(dashboard?.comercio?.plan_precio_local)}</p>
              </div>
              <p className="text-sm text-muted-foreground">
                La comisión puede ser revisada y ajustada periódicamente. Serás notificado de cualquier cambio.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={aceptarComision}>
              Acepto los términos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* DIGGII History Modal */}
      <DiggiiHistory 
        isOpen={showDiggiiHistory} 
        onClose={() => setShowDiggiiHistory(false)} 
      />
    </div>
  );
};

export default ComercioDashboard;
